import React, { useEffect, useRef, useState } from "react";
import TextInputField from "../CommonInputFields/TextInputField";
import PrimaryButton from "../CommonButtons/PrimaryButton";
import { linkIdentifier } from "../../../utils/linkIdentifier";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../utils/configurationProvider";
import IconRoundButton from "../CommonButtons/IconRoundButton";
import ICONS from "../../../assets/Icons";

// Suggestions of props for component while using the components -------------------------------------
/**
 * @typedef {((e: React.ChangeEvent<HTMLInputElement>) => void)} OnInputEventHandler
 */
/**
 * @typedef {((e: React.MouseEvent<HTMLButtonElement, MouseEvent>?) => void)} OnClickEventHandler
 */
/**
 * @typedef {("button" | "submit")} BtnType
 */
/**
 * @typedef {("white" | "black")} HoverOption
 */
/**
 * @typedef {("default" | "outlined" | "filled")} VariantType
 */
/**
 * @param {Object} props
 * @param {string} props.headerTitle - Main title text to display at the top of popup
 * @param {string} [props.headerFontWeight] - Font weight for header title eg:- "normal", "semibold", "bold"
 * @param {string} [props.headerFontColor] - Text color class for header eg:- "text-primary", "text-secondary", "failed"
 * @param {string} [props.textSize] - Font size class for header eg:- "font-sm", "font-md", "font-lg"
 * @param {string} [props.headerWithIcon] - Icon/SVG image path to display next to header title
 * @param {string} [props.secondHeaderTitle] - Optional secondary header text below main title
 * @param {string} [props.secondHeaderFontWeight] - Font weight for secondary header eg:- "normal", "semibold"
 * @param {string} [props.secondHeaderFontColor] - Text color class for secondary header eg:- "text-primary"
 * @param {string} [props.secondHeaderTextSize] - Font size class for secondary header eg:- "font-sm"
 * @param {string} [props.subtext] - Subtitle/description text below headers
 * @param {string[]} [props.bulletPoints] - Array of strings to display as bullet points below secondHeaderTitle
 * @param {boolean} [props.isTextInputField] - Show/hide text input field in popup (true/false)
 * @param {string} [props.inputPlaceholder] - Placeholder text for input field
 * @param {VariantType} [props.inputVariant] - Input field variant type eg:- "default", "outlined", "filled"
 * @param {string} [props.inputBgColor] - Background color for input field (CSS variable eg:- "var(--primary-bg)")
 * @param {string} [props.inputFgColor] - Foreground/text color for input field (CSS variable)
 * @param {string} [props.inputAdornment] - Icon/text to show at the end of input field (optional)
 * @param {OnInputEventHandler} [props.inputOnInput] - Callback function when input value changes eg:- (e) => setValue(e.target.value)
 * @param {string} [props.inputValue] - Controlled input value state
 * @param {string} [props.inputFontKind] - Font size for input field eg:- "small", "medium", "large"
 * @param {string} [props.inputName] - Name attribute for input field
 * @param {boolean} [props.inputIsDisabled] - Disable input field (true/false)
 * @param {boolean} [props.inputIsFullWidth] - Make input field full width (true/false)
 * @param {OnClickEventHandler} [props.inputOnAdornmentClick] - Callback when adornment icon is clicked
 * @param {import("react").FocusEventHandler} [props.inputOnFocus] - Callback when input is focused
 * @param {import("react").FocusEventHandler} [props.inputOnBlur] - Callback when input is blurred
 * @param {OnClickEventHandler} [props.inputOnAdornmentClick] - Callback when adornment icon is clicked
 * @param {string} [props.noteText] - Note/help text to display below input field
 * @param {string} props.btnOneText - Text to display on first button (left button)
 * @param {BtnType} [props.btnOneBtnType] - Button type for button one eg:- "button" or "submit"
 * @param {OnClickEventHandler} props.btnOneHandleFunction - Handle function when button one is clicked
 * @param {boolean} [props.btnOneIsDisabled] - Disable button one (true/false)
 * @param {string} [props.btnOneEnabledTextColor] - Text color when button one is enabled (CSS variable)
 * @param {string} [props.btnOneDisabledTextColor] - Text color when button one is disabled (CSS variable)
 * @param {string} [props.btnOneEnabledBgColor] - Background color when button one is enabled (CSS variable)
 * @param {string} [props.btnOneDisabledBgColor] - Background color when button one is disabled (CSS variable)
 * @param {string} [props.btnOneOutLineColor] - Border/outline color for button one (CSS variable)
 * @param {boolean} [props.btnOneFullWidthRequired] - Make button one full width (true/false)
 * @param {HoverOption} [props.btnOneHoverOption] - Hover effect option for button one eg:- "white" or "black"
 * @param {boolean} [props.btnOneIsLoading] - Show loading state on button one (true/false)
 * @param {React.RefObject} [props.btnOneButtonRef] - Button ref for button one (optional)
 * @param {boolean} [props.btnOneInActiveVariantNeeded] - Use inactive variant for button one (true/false)
 * @param {boolean} [props.btnOneIsLoaderNeeded] - Show loader on button one (true/false)
 * @param {string} props.btnTwoText - Text to display on second button (right button)
 * @param {BtnType} [props.btnTwoBtnType] - Button type for button two eg:- "button" or "submit"
 * @param {OnClickEventHandler} props.btnTwoHandleFunction - Handle function when button two is clicked
 * @param {boolean} [props.btnTwoIsDisabled] - Disable button two (true/false)
 * @param {string} [props.btnTwoEnabledTextColor] - Text color when button two is enabled (CSS variable)
 * @param {string} [props.btnTwoDisabledTextColor] - Text color when button two is disabled (CSS variable)
 * @param {string} [props.btnTwoEnabledBgColor] - Background color when button two is enabled (CSS variable)
 * @param {string} [props.btnTwoDisabledBgColor] - Background color when button two is disabled (CSS variable)
 * @param {string} [props.btnTwoOutLineColor] - Border/outline color for button two (CSS variable)
 * @param {boolean} [props.btnTwoFullWidthRequired] - Make button two full width (true/false)
 * @param {HoverOption} [props.btnTwoHoverOption] - Hover effect option for button two eg:- "white" or "black"
 * @param {boolean} [props.btnTwoIsLoading] - Show loading state on button two (true/false)
 * @param {React.RefObject} [props.btnTwoButtonRef] - Button ref for button two (optional)
 * @param {boolean} [props.btnTwoInActiveVariantNeeded] - Use inactive variant for button two (true/false)
 * @param {boolean} [props.btnTwoIsLoaderNeeded] - Show loader on button two (true/false)
 * @returns {JSX.Element}
 */
const SmallPopupWithBulletPoints = ({
  headerTitle,
  headerWithIcon,
  secondHeaderTitle,
  secondHeaderFontWeight,
  isTextInputField,
  noteText,
  subtext,
  inputPlaceholder,
  inputVariant,
  inputBgColor,
  inputFgColor,
  inputAdornment,
  inputOnInput,
  inputValue,
  inputFontKind,
  inputName,
  inputIsDisabled,
  inputIsFullWidth,
  inputOnAdornmentClick,
  inputOnFocus,
  inputOnBlur,
  btnOneText,
  btnOneBtnType,
  btnOneDisabledTextColor,
  btnOneDisabledBgColor,
  btnOneIsLoading,
  btnOneButtonRef,
  btnOneInActiveVariantNeeded,
  btnOneIsLoaderNeeded,
  btnOneHandleFunction,
  btnOneEnabledBgColor,
  btnOneOutLineColor,
  btnOneEnabledTextColor,
  btnOneIsDisabled,
  btnOneHoverOption,
  btnOneFullWidthRequired,
  btnTwoText,
  btnTwoBtnType,
  btnTwoDisabledTextColor,
  btnTwoIsLoading,
  btnTwoButtonRef,
  btnTwoInActiveVariantNeeded,
  btnTwoIsLoaderNeeded,
  btnTwoHandleFunction,
  btnTwoEnabledBgColor,
  btnTwoDisabledBgColor,
  btnTwoOutLineColor,
  btnTwoEnabledTextColor,
  btnTwoIsDisabled,
  btnTwoFullWidthRequired,
  btnTwoHoverOption,
  bulletPoints,
  noteColor,
  subtextwithbullet,
  closeIconImage,
  closeModal,
}) => {
  const [animate, setAnimate] = useState(false);
  const applyButtonRef = useRef(null);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [btnTwoIsDisabled, btnTwoIsLoading]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !btnTwoIsDisabled && !btnTwoIsLoading) {
      if (btnTwoButtonRef) {
        btnTwoButtonRef.current.click();
      } else {
        applyButtonRef.current.click();
      }
    }
  };

  const toSentenceCase = (text = "") =>
    text.charAt(GlobalConfiguration?.lengthCheckZero).toUpperCase() +
    text.slice(GlobalConfiguration?.lengthCheckOne).toLowerCase();

  const needsFullStop = (text = "") => !text.trim().endsWith(".");
  return (
    <div
      className="flex w-full justify-center items-center m-auto top-1/2 left-1/2 fixed z-z-index-modal p-padding-sm"
      style={{ transform: "translate(-50%, -50%)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={`h-fit w-full overflow-hidden max-h-graphic_rectangle_height_xl Qmd:w-small-popup-width flex flex-col px-padding-md py-padding-md bg-primary-bg rounded-border-radius-md animate-none ${animate ? "Qmd:animate-slideInFromLeft" : "Qmd:animate-slideInFromRight"} `}
      >
        <div className="">
          <div
            className={`flex flex-col ${subtextwithbullet === "Y" ? "gap-gap-xs" : "gap-gap-sm"} `}
          >
            <div className="flex gap-gap-sm justify-between">
              <div className="flex gap-gap-sm items-center ">
                <img
                  src={headerWithIcon}
                  alt=""
                  className={`h-icon-md w-icon-md ${!headerWithIcon && "hidden"} `}
                />

                <p className={`text-text-primary  text-font-md font-semibold`}>
                  {toSentenceCase(headerTitle)}
                </p>
              </div>
              {closeIconImage && (
                <div>
                  <IconRoundButton
                    enabledBgColor={"var(--primary-nav)"}
                    isLoading={false}
                    handleFunction={() => {
                      setAnimate(true);
                      setTimeout(() => {
                        closeModal();
                      }, IntervalConfiguration.intervalExtraSmall);
                    }}
                    icon={closeIconImage || ICONS?.CLOSE}
                    hoverOption={"white"}
                    btnType={"button"}
                    isDisabled={false}
                    buttonRef={null}
                    title={"Close"}
                    disabledBgColor={""}
                  />
                </div>
              )}
            </div>
            <div className={`${!secondHeaderTitle && "hidden"}`}>
              <p
                className={`text-font-sm text-text-primary ${secondHeaderFontWeight ? " font-normal" : "font-semibold"}  `}
              >
                {linkIdentifier(secondHeaderTitle)}
              </p>
            </div>
            <div className="flex flex-col">
              {subtext && (
                <span className="text-font-xs text-text-secondary font-normal">
                  {linkIdentifier(subtext)}
                  {needsFullStop(subtext) && <span>.</span>}
                </span>
              )}
              <div className={`${!isTextInputField && "hidden"} pt-padding-md`}>
                <div
                  className={`items-center  ${isTextInputField ? "flex" : "hidden"}`}
                >
                  <TextInputField
                    bgColor={inputBgColor}
                    fgColor={inputFgColor}
                    placeholder={inputPlaceholder}
                    variant={inputVariant}
                    fontKind={inputFontKind}
                    value={inputValue}
                    isFullWidth={inputIsFullWidth}
                    onInput={inputOnInput}
                    disabled={inputIsDisabled}
                    adornment={inputAdornment}
                    onFocus={inputOnFocus}
                    onBlur={inputOnBlur}
                    name={inputName}
                    {...(inputAdornment && inputOnAdornmentClick
                      ? { onAdornmentClick: inputOnAdornmentClick }
                      : {})}
                  />
                </div>
              </div>
              {noteText && (
                <div
                  className={`${noteColor ? `text-${noteColor}` : "text-text-secondary"} text-font-xs font-normal leading-normal pt-padding-xs`}
                >
                  <span>Note: </span>
                  {noteText && (
                    <span>
                      {linkIdentifier(noteText)}
                      {needsFullStop(noteText) && <span>.</span>}
                    </span>
                  )}
                </div>
              )}
              {bulletPoints &&
                bulletPoints?.length > GlobalConfiguration?.lengthCheckZero && (
                  <ul
                    className={`list-disc flex flex-col ${subtextwithbullet === "Y" ? "" : "gap-gap-xs"} pl-padding-md ${subtextwithbullet === "Y" ? "pt-padding-sm" : "pt-padding-base"}`}
                  >
                    {bulletPoints.map((point, index) => {
                      const isNested =
                        typeof point === "object" &&
                        point !== null &&
                        "text" in point;
                      const pointText = isNested ? point.text : point;
                      const subPoints = isNested ? point.subPoints : null;

                      return (
                        <li
                          key={index}
                          className={`text-font-xs text-text-secondary font-normal ${
                            subPoints?.length >
                            GlobalConfiguration?.lengthCheckZero
                              ? "mt-margin-sm"
                              : ""
                          }`}
                        >
                          {typeof pointText === "string"
                            ? linkIdentifier(pointText)
                            : pointText}
                          {subPoints &&
                            subPoints?.length >
                              GlobalConfiguration?.lengthCheckZero && (
                              <ul className="list-none flex flex-col gap-gap-xs  mt-margin-xs">
                                {/* {subPoints.map((subPoint, subIndex) => (
                                  <li
                                    key={subIndex}
                                    className="text-font-xs text-text-secondary font-normal"
                                  >
                                    -{" "}
                                    {typeof subPoint === "string"
                                      ? linkIdentifier(subPoint)
                                      : subPoint}
                                  </li>
                                ))} */}

                                {subPoints.map((subPoint, subIndex) => (
                                  <li
                                    key={subIndex}
                                    className={`${
                                      typeof subPoint === "object" &&
                                      subPoint?.link
                                        ? "text-font-xs"
                                        : subPoint?.fontSize === "14px"
                                          ? "text-font-sm"
                                          : "text-font-xs"
                                    } ${
                                      typeof subPoint === "object" &&
                                      subPoint?.link
                                        ? "font-normal"
                                        : subPoint?.fontWeight === "semibold"
                                          ? "font-semibold"
                                          : "font-normal"
                                    } text-text-secondary`}
                                  >
                                    -{" "}
                                    {typeof subPoint === "object" &&
                                    subPoint?.link ? (
                                      <a
                                        href={subPoint.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline break-all"
                                        style={{ color: "var(--pending)" }}
                                      >
                                        {subPoint.text}
                                      </a>
                                    ) : (
                                      linkIdentifier(
                                        typeof subPoint === "object"
                                          ? subPoint.text
                                          : subPoint,
                                      )
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                        </li>
                      );
                    })}
                  </ul>
                )}
            </div>
          </div>
          {(btnOneText || btnTwoText) && (
            <div
              className={`w-full gap-gap-sm flex justify-end pt-padding-md `}
            >
              <PrimaryButton
                btnText={btnOneText}
                btnType={btnOneBtnType}
                handleFunction={() => {
                  if (!btnOneIsDisabled) {
                    setAnimate(true);
                    setTimeout(() => {
                      btnOneHandleFunction();
                    }, IntervalConfiguration.intervalSemiMid);
                  }
                }}
                isDisabled={btnOneIsDisabled}
                enabledTextColor={btnOneEnabledTextColor}
                disabledTextColor={btnOneDisabledTextColor}
                enabledBgColor={btnOneEnabledBgColor}
                disabledBgColor={btnOneDisabledBgColor}
                outLineColor={btnOneOutLineColor}
                fullWidthRequired={btnOneFullWidthRequired}
                hoverOption={btnOneHoverOption}
                isLoading={btnOneIsLoading}
                buttonRef={btnOneButtonRef}
                inActiveVariantNeeded={btnOneInActiveVariantNeeded}
                isLoaderNeeded={btnOneIsLoaderNeeded}
              />
              <PrimaryButton
                btnText={btnTwoText}
                handleFunction={() => {
                  if (!btnTwoIsDisabled) {
                    setAnimate(true);
                    setTimeout(() => {
                      btnTwoHandleFunction();
                    }, IntervalConfiguration.intervalSemiMid);
                  }
                }}
                enabledBgColor={btnTwoEnabledBgColor}
                outLineColor={btnTwoOutLineColor}
                enabledTextColor={btnTwoEnabledTextColor}
                isDisabled={btnTwoIsDisabled}
                fullWidthRequired={btnTwoFullWidthRequired}
                hoverOption={btnTwoHoverOption}
                btnType={btnTwoBtnType}
                disabledTextColor={btnTwoDisabledTextColor}
                disabledBgColor={btnTwoDisabledBgColor}
                isLoading={btnTwoIsLoading}
                buttonRef={btnTwoButtonRef || applyButtonRef}
                inActiveVariantNeeded={btnTwoInActiveVariantNeeded}
                isLoaderNeeded={btnTwoIsLoaderNeeded}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmallPopupWithBulletPoints;
